// Shared geocoding utility used by <location-list> and <location-map-modal>.
//
// Caches resolved coordinates in localStorage forever (keyed by the full query
// string), and de-duplicates in-flight requests so concurrent callers for the
// same query share one network call.

const GEOCODE_ENDPOINT = 'https://maps.googleapis.com/maps/api/geocode/json'
const CACHE_PREFIX = 'salad-geocode:'

export interface Coords {
	lat: number
	lng: number
}

interface CacheEntry {
	lat: number
	lng: number
	ts: number
}

const inflight = new Map<string, Promise<Coords | undefined>>()

function cacheKey(query: string): string {
	return CACHE_PREFIX + query.trim().toLowerCase().replace(/\s+/g, ' ')
}

function readCache(query: string): Coords | undefined {
	try {
		const raw = window.localStorage.getItem(cacheKey(query))
		if (!raw) return undefined
		const entry = JSON.parse(raw) as CacheEntry
		if (typeof entry.lat !== 'number' || typeof entry.lng !== 'number') return undefined
		return { lat: entry.lat, lng: entry.lng }
	} catch {
		return undefined
	}
}

function writeCache(query: string, coords: Coords) {
	try {
		const entry: CacheEntry = { lat: coords.lat, lng: coords.lng, ts: Date.now() }
		window.localStorage.setItem(cacheKey(query), JSON.stringify(entry))
	} catch {
		// localStorage may be unavailable (private mode, quota) — ignore
	}
}

export async function geocodeAddress(query: string, googleKey: string): Promise<Coords | undefined> {
	const cleaned = query.trim()
	if (!cleaned || !googleKey) return undefined

	const cached = readCache(cleaned)
	if (cached) return cached

	const key = cacheKey(cleaned)
	const existing = inflight.get(key)
	if (existing) return existing

	const promise = (async (): Promise<Coords | undefined> => {
		const url = `${GEOCODE_ENDPOINT}?address=${encodeURIComponent(cleaned)}&key=${encodeURIComponent(googleKey)}`
		const response = await fetch(url)
		if (!response.ok) {
			console.warn(`[geocode] HTTP ${response.status} for "${cleaned}"`)
			return undefined
		}
		const body = (await response.json()) as {
			status: string
			error_message?: string
			results: Array<{ geometry: { location: { lat: number; lng: number } } }>
		}
		if (body.status !== 'OK') {
			console.warn(
				`[geocode] status=${body.status} for "${cleaned}"`,
				body.error_message ?? '(no error_message)',
				body
			)
			return undefined
		}
		if (!body.results.length) {
			console.warn(`[geocode] OK but no results for "${cleaned}"`, body)
			return undefined
		}
		const loc = body.results[0].geometry.location
		const coords: Coords = { lat: loc.lat, lng: loc.lng }
		console.log(`[geocode] resolved "${cleaned}" →`, coords)
		writeCache(cleaned, coords)
		return coords
	})()
		.catch((err: unknown) => {
			console.warn(`[geocode] fetch threw for "${cleaned}"`, err)
			return undefined
		})
		.finally(() => {
			inflight.delete(key)
		})

	inflight.set(key, promise)
	return promise
}

// Build a Google-friendly query for a store location. Adding the city gives
// the geocoder enough context to disambiguate e.g. UK vs US postal codes.
export function locationGeocodeQuery(postcode: string, city: string): string {
	const p = (postcode || '').trim()
	const c = (city || '').trim()
	if (p && c) return `${p}, ${c}`
	return p || c
}
